import { useEffect } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ChevronRight, Folder as FolderIcon } from '@mui/icons-material';
import { useLazyGetFolderItemsQuery, type FileStorageItem } from '../../api/fileStorageApi';

type PathItem = { id: string | undefined; name: string };

type TransferFolderNodeProps = {
  folder: FileStorageItem;
  depth: number;
  ancestors: PathItem[];
  destinationFolderId: string | undefined;
  expandedFolderIds: Set<string>;
  disabledFolderIds: Set<string>;
  onToggleExpand: (folderId: string) => void;
  onSelectDestination: (path: PathItem[]) => void;
};

export const TransferFolderNode = ({
  folder,
  depth,
  ancestors,
  destinationFolderId,
  expandedFolderIds,
  disabledFolderIds,
  onToggleExpand,
  onSelectDestination,
}: TransferFolderNodeProps) => {
  const [trigger, { data }] = useLazyGetFolderItemsQuery();

  const isExpanded = expandedFolderIds.has(folder.id);
  const showChevron = folder.isFolder && folder.hasSubDirectories;

  useEffect(() => {
    if (!isExpanded || !folder.isFolder || !folder.hasSubDirectories) return;
    void trigger({ nodeKey: folder.id, storageType: 'All files' });
  }, [isExpanded, folder.id, folder.hasSubDirectories, folder.isFolder, trigger]);

  const subFolders = (data?.fileItems ?? []).filter((item) => item.isFolder);
  const isDisabled = disabledFolderIds.has(folder.id);
  const nodePath = [...ancestors, { id: folder.id, name: folder.name }];

  return (
    <>
      <ListItemButton
        dense
        selected={destinationFolderId === folder.id}
        onClick={() => {
          if (isDisabled) return;
          onSelectDestination(nodePath);
        }}
        sx={{
          pl: 1 + depth * 1.5,
          pr: 0.5,
          py: 0.5,
          opacity: isDisabled ? 0.55 : 1,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        <Box sx={{ width: 22, display: 'inline-flex', justifyContent: 'center', mr: 0.5 }}>
          {showChevron && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(folder.id);
              }}
              sx={{
                p: 0.25,
                transition: 'transform 180ms ease',
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          )}
        </Box>
        <ListItemIcon sx={{ minWidth: 24 }}>
          <FolderIcon fontSize="small" color="warning" />
        </ListItemIcon>
        <ListItemText
          primary={folder.name}
          primaryTypographyProps={{
            variant: 'body2',
            noWrap: true,
            color: isDisabled ? 'text.disabled' : undefined,
          }}
          sx={{ mr: 0 }}
        />
      </ListItemButton>

      <Collapse in={isExpanded} unmountOnExit>
        {subFolders.map((subFolder) => (
          <TransferFolderNode
            key={subFolder.id}
            folder={subFolder}
            depth={depth + 1}
            ancestors={nodePath}
            destinationFolderId={destinationFolderId}
            expandedFolderIds={expandedFolderIds}
            disabledFolderIds={disabledFolderIds}
            onToggleExpand={onToggleExpand}
            onSelectDestination={onSelectDestination}
          />
        ))}
      </Collapse>
    </>
  );
};
